import { Button } from "@tellescope/design-system";
import { Combobox, type ComboboxOption } from "@tellescope/ui";
import { useHealthieAppointmentTypesQuery } from "@tellescope/tanstack-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface HealthieTemplatePickerProps {
  id?: string;
  value: string | undefined;
  onChange: (healthieTemplateId: string) => void;
}

export const HealthieTemplatePicker = ({ id, value, onChange }: HealthieTemplatePickerProps) => {
  const { t } = useTranslation("calendar");
  const appointmentTypesQuery = useHealthieAppointmentTypesQuery();
  const isPending = appointmentTypesQuery.isPending;

  const options = useMemo<ComboboxOption[]>(() => {
    const appointmentTypes = appointmentTypesQuery.data?.data ?? [];

    return appointmentTypes.map(({ id, name, length }) => ({
      value: id,
      label: name,
      description: t("{{count}} minutes · ID {{id}}", { count: length ?? 0, id }),
    }));
  }, [appointmentTypesQuery.data?.data, t]);

  const selectedOption = useMemo<ComboboxOption | undefined>(() => {
    if (!value) return undefined;
    return options.find((option) => option.value === value) ?? { value, label: value };
  }, [options, value]);

  return (
    <Combobox
      id={id}
      value={selectedOption}
      onChange={(option) => onChange(option?.value ?? "")}
      options={options}
      isLoading={isPending}
      placeholder={t("Select a Healthie template...")}
      searchPlaceholder={t("Search Healthie templates...")}
      loadingText={t("Loading Healthie templates...")}
      emptyText={
        appointmentTypesQuery.isError ? t("Could not load Healthie templates") : t("No Healthie templates found")
      }
      className="w-full"
    >
      {isPending ? (
        <Button variant="outline" color="muted" loading className="w-full justify-start font-normal h-8 py-2">
          {t("Loading Healthie templates...")}
        </Button>
      ) : undefined}
    </Combobox>
  );
};
